import { useState } from "react";
import { useEffect } from "react";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Typography, Box } from "@mui/material";
import I18n from "i18next";
import subMonths from "date-fns/subMonths";
import format from "date-fns/format";
import startOfMonth from "date-fns/startOfMonth";
import endOfMonth from "date-fns/endOfMonth";

import { MzTabContext, MzTabPanel } from "@markaaz/ui/MzTabControls";
import MzAutocomplete from "@markaaz/ui/MzAutocomplete";

import { loadOnboardingData, selectVerifiedApplication } from "store/slices/overview/onboardingPage";
import {
  selectCreditScore,
  selectBusiness,
  loadCreditScoreData,
  loadSellersData,
  loadMonitoringData
} from "store/slices/overview/monitoringPage";
import {
  loadPortfolioData,
  selectOfferedCustomer,
  selectAcceptedCustomer,
  selectActiveCustomer,
  loadOfferedCustomerData,
  loadAcceptedustomerData,
  loadActiveCustomerData
} from "store/slices/overview/portfolioPage";

import { selectPartnersData, selectPortfolios, selectUser } from "store/slices/app";

import ApplicationOnboardingTab from "components/overview/ApplicationOnboarding.js/ApplicationOnboardingTab";
import PortfolioPartnerSolution from "components/overview/PortfolioPartnerSolution/PortfolioPartnerSolution";
import Monitoring from "components/overview/Monitoring/Monitoring";
import MonitoringReportFrequency from "components/common/MonitoringReportFrequency";
import getTimeRange from "utils/getTimeRange";

const sx = {
  heading: {
    color: (theme) => theme.palette.darkBlue.c100,
    marginBottom: "40px"
  },
  headerContent: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center"
  }
};

const Overview = () => {
  const { tab } = useParams();
  const [selectedPortfolioId, setSelectedPortfolioId] = useState(null);

  const dispatch = useDispatch();

  const verifiedApplicationData = useSelector(selectVerifiedApplication);
  const offeredCustomerData = useSelector(selectOfferedCustomer);
  const acceptedCustomerData = useSelector(selectAcceptedCustomer);
  const activeCustomerData = useSelector(selectActiveCustomer);
  const creditScoreData = useSelector(selectCreditScore);
  const businessData = useSelector(selectBusiness);
  const portfolios = useSelector(selectPortfolios);
  const userData = useSelector(selectUser);
  const partnersData = useSelector(selectPartnersData);

  const startDate = businessData?.data?.nextUpdateDate;
  const endDate = businessData?.data?.mostRecentComplianceFlagChangeDate;

  const start = format(startOfMonth(subMonths(new Date(), 11)), "yyyy-MM-dd");
  const end = format(endOfMonth(new Date()), "yyyy-MM-dd");
  const loadData = (userData.isSuperUser && partnersData.isPartnerChanged) || !userData.isSuperUser;

  useEffect(() => {
    loadData && dispatch(loadOnboardingData(start, end, userData.selectedPartner.id));
    loadData && dispatch(loadPortfolioData(start, end, userData.selectedPartner.id));

    if (selectedPortfolioId) {
      dispatch(loadMonitoringData(start, end, selectedPortfolioId, userData.selectedPartner.id));
    }
  }, [start, end, selectedPortfolioId, userData.selectedPartner.id, loadData, dispatch]);

  function handlePorfolioChange(e, value) {
    setSelectedPortfolioId(value ? value.id : null);
  }

  function handleTimeRange(option, tileName) {
    const data = getTimeRange(option);
    const start = data.start;
    const end = data.end;
    tileName === "verifiedApplications" && dispatch(loadOnboardingData(start, end, userData.selectedPartner.id));
    tileName === "monitoredBusiness" &&
      dispatch(loadSellersData(start, end, selectedPortfolioId, userData.selectedPartner.id));
    tileName === "businessCreditScore" &&
      dispatch(loadCreditScoreData(start, end, selectedPortfolioId, userData.selectedPartner.id));
    tileName === "offersExtended" &&
      dispatch(dispatch(loadOfferedCustomerData(start, end, userData.selectedPartner.id)));
    tileName === "offersAccepted" && dispatch(loadAcceptedustomerData(start, end, userData.selectedPartner.id));
    tileName === "activeCustomers" && dispatch(loadActiveCustomerData(start, end, userData.selectedPartner.id));
  }

  return (
    <Box>
      <Box sx={sx.headerContent}>
        <Typography variant="h4" sx={sx.heading}>
          {tab === "onboarding"
            ? I18n.t("overview.applicationOnboarding")
            : tab === "monitoring"
            ? I18n.t("overview.monitoring")
            : I18n.t("overview.portfolioPartner")}
        </Typography>
      </Box>

      <MzTabContext value={tab}>
        <MzTabPanel value="onboarding">
          <ApplicationOnboardingTab verifiedApplicationData={verifiedApplicationData} onClick={handleTimeRange} />
        </MzTabPanel>

        <MzTabPanel value="monitoring">
          {selectedPortfolioId && (
            <MonitoringReportFrequency
              placeHolderText1="Date of next update"
              placeHolderText2="Date of most recent compliance flag"
              startDate={
                startDate
                  ? new Date(startDate).toLocaleDateString("en-US", {
                      year: "numeric",
                      month: "2-digit",
                      day: "2-digit"
                    })
                  : " "
              }
              endDate={
                endDate
                  ? new Date(endDate).toLocaleDateString("en-US", {
                      year: "numeric",
                      month: "2-digit",
                      day: "2-digit"
                    })
                  : "No data"
              }
              monitoringKey="monitorBusiness"
            />
          )}

          <div style={{ maxWidth: "420px", marginTop: "40px", marginBottom: "40px" }}>
            <MzAutocomplete
              label="Portfolio"
              placeholder="Select"
              options={portfolios}
              onChange={handlePorfolioChange}
              defaultValue={null}
            />
          </div>

          <Monitoring
            creditScoreData={creditScoreData}
            businessData={businessData}
            selectedPortfolioId={selectedPortfolioId}
            onClick={handleTimeRange}
          />
        </MzTabPanel>

        <MzTabPanel value="portfolio-partner">
          <PortfolioPartnerSolution
            offeredCustomerData={offeredCustomerData}
            acceptedCustomerData={acceptedCustomerData}
            activeCustomerData={activeCustomerData}
            onClick={handleTimeRange}
          />
        </MzTabPanel>
      </MzTabContext>
    </Box>
  );
};

export default Overview;
